import { Inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Observer } from 'rxjs';
import { filter, mapTo, take } from 'rxjs/operators';
import { WebSocketConnectionState, WebSocketServiceBase, WebSocketServiceConfig, WSServiceFactory, WS_SERVICE_FACTORY } from 'types';
import { HWServiceConfig } from './hw-service-config';
import { CryptoServiceState } from './hw-service-model';

export interface CryptoCertificate {
  'token-serial': string;
  'key-id': string;
  'cert-pem': string;
}

export interface TokenInfo {
  'certificates': CryptoCertificate[];
  'token-count': number;
}

enum ResponseType {
  None,
  GetCertificates,
  Sign,
}

@Injectable()
export class CryptoService {
  public get connectionState$(): Observable<CryptoServiceState> {
    return this.connectionStateSubject.asObservable();
  }

  private get canConnect(): boolean {
    return this.serviceState === CryptoServiceState.Closed;
  }

  private get canDisconnect(): boolean {
    return this.serviceState !== CryptoServiceState.Closed;
  }

  private get canPerformOperation(): boolean {
    return this.canDisconnect && this.responseType === ResponseType.None;
  }

  private responseType: ResponseType = ResponseType.None;
  private serviceState: CryptoServiceState = CryptoServiceState.Closed;
  private connectionStateSubject: BehaviorSubject<CryptoServiceState>;
  private socket: WebSocketServiceBase;

  private certificatesObserver: Observer<TokenInfo> | null = null;
  private signObserver: Observer<string> | null = null;

  public constructor(config: HWServiceConfig, @Inject(WS_SERVICE_FACTORY) createWebSocket: WSServiceFactory) {
    this.connectionStateSubject = new BehaviorSubject(this.serviceState);
    this.socket = createWebSocket(new WebSocketServiceConfig(config.getHWServiceUrl(), config.getCryptoServiceProtocol()));
    this.socket.messages$.subscribe(
      (message) => this.onSocketMessageReceived(message),
      (error) => this.onSocketMessageReceived({ result: false, error: error }),
    );
    this.socket.connectionState$.subscribe(
      (state) => this.onSocketConnectionStateChanged(state),
      (error) => this.connectionStateSubject.error(error),
    );
  }

  public connect$(): Observable<void> {
    this.connect();
    return this.socket.connectionState$.pipe(
      take(1),
      filter((x) => x === WebSocketConnectionState.Opened),
      mapTo(undefined),
    );
  }

  public connect(): void {
    if (this.canConnect) {
      this.socket.connect();
    } else {
      this.notifyOperationDenied('CONNECT');
    }
  }

  public disconnect(): void {
    if (this.canDisconnect) {
      this.socket.disconnect();
    } else {
      this.notifyOperationDenied('DISCONNECT');
    }
  }

  public getCertificates(): Observable<TokenInfo> {
    return new Observable((observer) => {
      if (this.canPerformOperation) {
        this.certificatesObserver = observer;
        this.responseType = ResponseType.GetCertificates;
        this.socket.send({ command: 'get-certificates' });
      } else {
        this.notifyOperationDenied('GET_CERTIFICATES', observer);
      }
    });
  }

  public sign(data: string, tokenSerial: string, keyId: string, pin: string): Observable<string> {
    return new Observable((observer) => {
      if (this.canPerformOperation) {
        this.signObserver = observer;
        this.responseType = ResponseType.Sign;
        this.socket.send({
          'command': 'sign',
          'data': data,
          'token-serial': tokenSerial,
          'key-id': keyId,
          'pin': pin,
        });
      } else {
        this.notifyOperationDenied('SIGN', observer);
      }
    });
  }

  private onSocketMessageReceived(message: any): void {
    switch (this.responseType) {
      case ResponseType.GetCertificates:
        this.onGetCertificatesResponseReceived(message);
        break;
      case ResponseType.Sign:
        this.onSignResponseReceived(message);
        break;
    }
  }

  private onGetCertificatesResponseReceived(message: any): void {
    if (!this.certificatesObserver) return;

    if (message.result) {
      this.responseType = ResponseType.None;
      this.certificatesObserver.next({
        'certificates': message.certificates,
        'token-count': message['token-count'],
      });
      this.certificatesObserver.complete();
      this.certificatesObserver = null;
    } else {
      this.responseType = ResponseType.None;
      this.certificatesObserver.error(message.error);
      this.certificatesObserver = null;
    }
  }

  private onSignResponseReceived(message: any): void {
    if (!this.signObserver) return;

    if (message.result) {
      this.responseType = ResponseType.None;
      this.signObserver.next(message.signature);
      this.signObserver.complete();
      this.signObserver = null;
    } else {
      this.responseType = ResponseType.None;
      this.signObserver.error(message.error);
      this.signObserver = null;
    }
  }

  private onSocketConnectionStateChanged(state: WebSocketConnectionState): void {
    switch (state) {
      case WebSocketConnectionState.Opened:
        this.setServiceState(CryptoServiceState.Ready);
        break;
      case WebSocketConnectionState.Connecting:
      case WebSocketConnectionState.Disconnecting:
      case WebSocketConnectionState.Closed:
        this.setServiceState(CryptoServiceState.Closed);
        break;
    }
  }

  private setServiceState(state: CryptoServiceState): void {
    if (this.serviceState !== state) {
      this.serviceState = state;
      this.connectionStateSubject.next(state);
    }
  }

  private notifyOperationDenied<T>(operation: string, observer: Observer<T> | null = null): void {
    const message =
      `Cannot perform operation "${operation}". ` +
      `Processing state: ${CryptoServiceState[this.serviceState]}. ` +
      `Expected response: ${ResponseType[this.responseType]}.`;

    if (observer) {
      observer.error(message);
    } else {
      this.connectionStateSubject.error(message);
    }
  }
}
